/**
 * ApiKeysPage — Settings → More → API Keys.
 *
 * Manages the user's PUBLIC-API keys (backend: POST /v2-api-keys). The
 * plaintext key exists exactly once, in the create response — it's surfaced in
 * a one-time modal with copy-to-clipboard and never stored or
 * logged client-side; the list only ever shows the masked prefix.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Copy, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { v2Service, type V2ApiKey } from '@/api/v2Service';
import { vConfirm } from '@/v2/dialogs';
import { toast } from '@/v2/toast';
import { cn } from '@/v2/utils';
import { formatKeyDate, formatLastUsed, quickstartCurl } from '@/v2/apiKeysHelpers';

const DOCS_URL = 'https://developers.vivideo.ai';

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (key: string, text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* best effort */ }
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };
  return { copied, copy };
}

export function ApiKeysPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const { copied, copy } = useCopy();

  const [keys, setKeys] = useState<V2ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [keyName, setKeyName] = useState('');
  // The one-time plaintext key from the create response; cleared on modal close.
  const [freshKey, setFreshKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      setKeys(await v2Service.listApiKeys(userId));
    } catch {
      toast.error(t('apiKeys.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => { void load(); }, [load]);

  const handleCreate = async () => {
    if (!userId || creating) return;
    setCreating(true);
    try {
      const created = await v2Service.createApiKey(userId, keyName.trim() || 'API key');
      setFreshKey(created.api_key);
      setKeyName('');
      void load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg === 'key_limit_reached' ? t('apiKeys.keyLimit') : t('apiKeys.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: V2ApiKey) => {
    const ok = await vConfirm(t('apiKeys.revokeConfirmBody'), {
      title: t('apiKeys.revokeConfirmTitle'),
      confirmLabel: t('apiKeys.revoke'),
      danger: true,
    });
    if (!ok || !userId) return;
    try {
      await v2Service.revokeApiKey(userId, key.key_id);
      toast.success(t('apiKeys.revokeSuccess'));
      void load();
    } catch {
      toast.error(t('apiKeys.revokeFailed'));
    }
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setFreshKey(null); // the plaintext never survives the modal
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
      <button
        onClick={() => navigate('/account')}
        className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> {t('account.sectionMore')}
      </button>

      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <KeyRound className="w-7 h-7 text-primary" />
          <h1 className="text-2xl font-extrabold text-text-primary">{t('apiKeys.title')}</h1>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" /> {t('apiKeys.createKey')}
        </button>
      </div>
      <p className="text-sm text-text-secondary mb-2">{t('apiKeys.subtitle')}</p>
      <a
        href={DOCS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary hover:underline"
      >
        {t('apiKeys.docsLink')} →
      </a>

      {/* Key list */}
      <div className="glass rounded-2xl mt-6 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-text-tertiary">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-text-tertiary text-center py-12 px-6">{t('apiKeys.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-text-tertiary border-b border-glass-border">
                <th className="px-5 py-3 font-semibold">{t('apiKeys.colName')}</th>
                <th className="px-5 py-3 font-semibold">{t('apiKeys.colKey')}</th>
                <th className="px-5 py-3 font-semibold hidden sm:table-cell">{t('apiKeys.colCreated')}</th>
                <th className="px-5 py-3 font-semibold hidden sm:table-cell">{t('apiKeys.colLastUsed')}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.key_id} className="border-b border-glass-border/50 last:border-0">
                  <td className={cn('px-5 py-3 text-text-primary', k.status === 'revoked' && 'line-through text-text-tertiary')}>
                    {k.name}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-text-secondary">{k.key_prefix}</td>
                  <td className="px-5 py-3 text-text-tertiary hidden sm:table-cell">{formatKeyDate(k.created_at)}</td>
                  <td className="px-5 py-3 text-text-tertiary hidden sm:table-cell">
                    {formatLastUsed(k.last_used_at, t('apiKeys.never'))}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {k.status === 'active' ? (
                      <button
                        onClick={() => void handleRevoke(k)}
                        className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> {t('apiKeys.revoke')}
                      </button>
                    ) : (
                      <span className="text-xs text-text-tertiary">{t('apiKeys.revoked')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quickstart */}
      <div className="glass rounded-2xl p-6 mt-6">
        <p className="text-xs text-text-tertiary uppercase tracking-wide mb-1">{t('apiKeys.quickstartTitle')}</p>
        <p className="text-sm text-text-secondary mb-3">{t('apiKeys.quickstartHint')}</p>
        <div className="flex items-start justify-between gap-4 bg-background/60 rounded-xl px-4 py-3">
          <pre className="text-xs text-primary-light font-mono whitespace-pre-wrap">{quickstartCurl()}</pre>
          <button
            onClick={() => void copy('curl', quickstartCurl())}
            className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors shrink-0"
          >
            {copied === 'curl' ? <Check className="w-3.5 h-3.5 text-primary" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'curl' ? t('apiKeys.copied') : t('apiKeys.copy')}
          </button>
        </div>
      </div>

      {/* Create / show-once modal */}
      {createOpen && createPortal(
        <div
          className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in"
          onClick={freshKey ? undefined : closeCreateModal}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-[#15151d] border border-white/10 p-6 shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {freshKey ? (
              <>
                <h2 className="text-lg font-bold text-text-primary mb-2">{t('apiKeys.createdTitle')}</h2>
                <p className="text-sm text-amber-400 mb-4">{t('apiKeys.createdWarning')}</p>
                <div className="flex items-center justify-between gap-3 bg-background/60 rounded-xl px-4 py-3 mb-5">
                  <code className="text-xs text-primary-light font-mono break-all">{freshKey}</code>
                  <button
                    onClick={() => { void copy('key', freshKey); toast.success(t('apiKeys.toastKeyCopied')); }}
                    className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors shrink-0"
                  >
                    {copied === 'key' ? <Check className="w-3.5 h-3.5 text-primary" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === 'key' ? t('apiKeys.copied') : t('apiKeys.copy')}
                  </button>
                </div>
                <button
                  onClick={closeCreateModal}
                  className="w-full py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  {t('apiKeys.done')}
                </button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-text-primary mb-4">{t('apiKeys.createKey')}</h2>
                <label className="block text-xs text-text-tertiary uppercase tracking-wide mb-2">
                  {t('apiKeys.nameLabel')}
                </label>
                <input
                  autoFocus
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
                  maxLength={60}
                  placeholder={t('apiKeys.namePlaceholder')}
                  className="w-full bg-background/60 border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-primary/60 mb-5"
                />
                <div className="flex gap-3">
                  <button
                    onClick={closeCreateModal}
                    className="flex-1 py-3 rounded-full glass text-sm font-semibold text-text-primary hover:bg-surface-light transition-colors"
                  >
                    {t('apiKeys.cancel')}
                  </button>
                  <button
                    onClick={() => void handleCreate()}
                    disabled={creating}
                    className="flex-1 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {creating ? t('apiKeys.creating') : t('apiKeys.createKey')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
